import { createSlide } from '@/modules/createSlide';
import { releaseMediaSrcIn } from '@/helpers/deferredMediaSrc';
import { navigateMediaItem } from '@/helpers/navigateMediaItem';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { groupMoveButton } from '@/modules/parts/desktop/groupMoveButton';
import { reelHeaderInitializing } from '@/helpers/reelHeaderInitializing';
import { activeVideoPlay } from "@/modules/actions/videoControls";
import { checkVolumeButtonState } from "@/helpers/volumeControl";
import { ctaButton } from '@/modules/parts/ctaButton';
import { clickCtaAction } from '@/modules/actions/clickCtaAction';
import { setupAutoPlayNextAction } from '@/modules/actions/desktop/setupAutoPlayNextAction';
import { slideClickNavigateAction } from "@/modules/actions/desktop/slideClickNavigateAction";
import { animateCurrentProgressBar, prepareCurrentProgressBar } from "@/modules/actions/headerLineDuration";
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import { applySlideScaling } from '@/modules/actions/slideScaleAction';
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { isTrackableMediaId } from "@/helpers/isTrackableMediaId";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { isMediaImageReady } from '@/helpers/mediaUtils';
import { maybeShowTargetingQuestionnaireForCurrentContent } from '@/helpers/maybeShowTargetingQuestionnaire';
import { injectFreqInsertIntoGroup, countNormalContentsSeen, unmarkUnseenFreqInserts } from '@/helpers/streamsTargeting';

// ─── ボタンの描画だけを行う関数 ─────────────────────────
export const renderGroupMoveButtons = () => {
  const modal = document.querySelector('.storeeel-modal');
  // 既存のボタンをクリア
  modal.querySelectorAll('.leftGroupButtonFrame, .rightGroupButtonFrame').forEach(el => el.remove());
  // 新しいボタンを挿入
  modal.insertAdjacentHTML('beforeend', groupMoveButton());
};

// ─── イベントバインドだけを行う関数 ───────────────────────
export const bindGroupMoveButtonEvents = (changeGroupFn) => {
  const prevBtn = document.querySelector('.leftGroupButtonFrame');
  const nextBtn = document.querySelector('.rightGroupButtonFrame');
  if (prevBtn) prevBtn.addEventListener('click', () => changeGroupFn('prev'));
  if (nextBtn) nextBtn.addEventListener('click', () => changeGroupFn('next'));
};

// ─── グループ移動アクション全体 ──────────────────────────
export const groupMoveButtonAction = () => {
  const swiper   = getSwiperInstance();
  const wrapper  = document.querySelector('.storeeel-swiper-group-wrapper .swiper-wrapper');

  // 実際にグループを切り替える処理
  const changeGroup = (direction) => {
    const prevGroupId = window.iconId;
    const prevMoviePosition = Number(window.moviePosition || 0);

    const groupItem = direction === 'prev' ? window.prevGroup : window.nextGroup;
    window.iconId = groupItem.id;
    window.moviePosition = 0;

    // circle 型: 頻度挿入状態を更新し、新グループに未注入なら注入する
    if (window.storeeelItems?.frame === 'circle') {
      const freqState = window._storeeelFreqInsertCircleState;
      if (freqState) {
        const groups = window.storeeelItems.contentGroups || [];
        const prevGroupIdx = groups.findIndex((g) => String(g.id) === String(prevGroupId));
        const newGroupIdx = groups.findIndex((g) => String(g.id) === String(groupItem.id));
        if (direction === 'next' && prevGroupIdx >= 0) {
          freqState.contentsSeen += countNormalContentsSeen(groups[prevGroupIdx], prevMoviePosition);
          // 未到達位置のfreqを displayedIds から除去し、次グループで再利用できるようにする
          unmarkUnseenFreqInserts(groups[prevGroupIdx], prevMoviePosition, freqState);
          // 前グループの最後がfreqだった場合、次グループ先頭へのpre-insertをスキップする
          freqState.skipNextPreInsert = groups[prevGroupIdx].contents[prevMoviePosition]?.isFrequencyInsertContent === true;
        }
        if (newGroupIdx >= 0 && !groups[newGroupIdx]._freqInsertInjected) {
          const injectedGroup = injectFreqInsertIntoGroup(groups[newGroupIdx], freqState);
          injectedGroup._freqInsertInjected = true;
          groups[newGroupIdx] = injectedGroup;
          window.storeeelItems = { ...window.storeeelItems, contentGroups: groups };
        }
      }
    }

    const contents = navigateMediaItem();
    // 動画リストを再描画（外すだけでは取得が止まらないことがあるため、先に動画を解放する）
    releaseMediaSrcIn(wrapper);
    wrapper.innerHTML = '';
    // 先頭以外は src を保留し、中央 → 隣の順に読み込む（pcMediaLoadWindow）
    contents.forEach((content, index) => {
      wrapper.appendChild(createSlide(content, { deferSrc: index !== 0 }));
    });

    swiper.update();
    swiper.slideTo(0);
    // スライド再描画後に縮尺を適用
    applySlideScaling();

    // 初期グループ名取得＆バナー初期化
    const groupName = contents[0]?.group_name || '';
    reelHeaderInitializing(contents.length, groupName);

    // ボタン再描画＋再バインド
    renderGroupMoveButtons();
    bindGroupMoveButtonEvents(changeGroup);

    // スライドの動画再生
    activeVideoPlay();

    // 音量状態を確認し、activeなスライドの音量を変更
    checkVolumeButtonState();

    // CTAボタンの再描写
    const allWrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .slide-media-wrapper');
    allWrappers.forEach((wrapper, index) => {
      const ctaHost = ctaButton(contents[index]);
      if (ctaHost) {
        wrapper.style.cursor = 'pointer';
        wrapper.appendChild(ctaHost);
      }
    });
    clickCtaAction();

    // 動画の再生が終わったら次のスライドへ
    setupAutoPlayNextAction();

    // スライドクリックで該当スライドに移動する処理を追加
    slideClickNavigateAction();

    // 進捗バーのアニメーションを開始
    const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
    showMediaLoadingShimmer(activeWrapper);
    const video = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper video.storeeel-movie');
    const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper img.storeeel-media-image');
    if (video) {
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }

      const cleanup = () => {
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }
      };

      const tryStartProgress = () => {
        if (video.paused || video.ended) return false;
        if (video.readyState < 2) return false;
        const duration = Number(video.duration);
        if (!Number.isFinite(duration) || duration <= 0) return false;
        animateCurrentProgressBar(duration, 0);
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
        return true;
      };

      // 再生開始まで進捗を開始しない
      prepareCurrentProgressBar(0);
      video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
      video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
      video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
      video.addEventListener('playing', video._onPlayingForProgress);
      tryStartProgress();
    } else if (image) {
      if (image._onLoadForProgress) {
        image.removeEventListener('load', image._onLoadForProgress);
        image._onLoadForProgress = null;
      }
      if (image._onErrorForProgress) {
        image.removeEventListener('error', image._onErrorForProgress);
        image._onErrorForProgress = null;
      }

      const cleanup = () => {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }
      };

      const tryStartProgress = () => {
        if (!isMediaImageReady(image)) return false;
        animateCurrentProgressBar(5, 0);
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
        return true;
      };

      // 読み込み完了後に進捗を開始する
      prepareCurrentProgressBar(0);
      image._onLoadForProgress = () => { tryStartProgress(); };
      image._onErrorForProgress = () => {
        hideMediaLoadingShimmer(activeWrapper);
        cleanup();
      };
      image.addEventListener('load', image._onLoadForProgress);
      image.addEventListener('error', image._onErrorForProgress);
      tryStartProgress();
    }

    // 計測とローカルストレージ保存
    let mediaName;
    let mediaId;
    let groupId;
    if (video) {
      mediaName = video.getAttribute('media-name');
      mediaId = video.getAttribute('media-id');
    } else if (image) {
      mediaName = image.getAttribute('media-name');
      mediaId = image.getAttribute('media-id');
    }
    if (window.storeeelItems.frame === "circle") {
      groupId = window.iconId
    } else {
      groupId = null
    }
    const mediaAssetType = activeWrapper?.dataset?.assetType;
    // media_id が空のイベント/ログは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
    if (isTrackableMediaId(mediaId)) {
      trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
        storeeelName: mediaName,
        groupId,
        mediaId,
        campaignId: window.storeeelItems.id,
        mediaAssetType,
      }));
      localStorageRepositories.get("mediaView").set(
        mediaId,
        window.storeeelItems.id,
        groupId,
        mediaAssetType
      );
    }
    // 無期限の既読フラグも保存（スライド型は保存しない）
    try {
      if (window.storeeelItems?.frame !== 'square') {
        const contentId = (video && video.getAttribute('content-id')) || (image && image.getAttribute('content-id'));
        if (contentId) {
          localStorageRepositories.get("contentSeen").set(contentId);
        }
        if (groupId) updateGroupSeenBorder(groupId);
      }
    } catch { void 0; }

    maybeShowTargetingQuestionnaireForCurrentContent();
  };

  // 初回描画＋バインド
  renderGroupMoveButtons();
  bindGroupMoveButtonEvents(changeGroup);
  // 初回描画後とスライド移動時に縮尺を当てる
  applySlideScaling();
  swiper.on('slideChange', applySlideScaling);
};
